import React, { useCallback, useRef, useState, useMemo } from 'react';
import { Button, makeStyles, Typography } from '@material-ui/core';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import i18n from 'i18n-js';

import { theme } from 'config/theme';
import { EditorTable, TableShape } from '../context';
import { useTableMapWizard } from '../context';
import MapEditorCanvas, { ZOOM_MIN, ZOOM_MAX, ZOOM_STEP } from '../components/MapEditorCanvas';
import TableFigure from '../components/TableFigure';
import SelectionHighlight from '../components/SelectionHighlight';
import SectionTitle from '../components/SectionTitle';
import {
    clampPointToArea,
    computeRoom,
    EditorArea,
    RoomRect,
    SizeMeters,
    snapPointMeters,
    tableFootprintPx,
} from '../mapGeometry';

const DRAG_THRESHOLD = 4;
const ERROR_COLOR = theme.palette.error.main;

const MAGENTA = theme.palette.primary.main;
const MAGENTA_LIGHT = 'rgba(153, 86, 126, 0.08)';
const MAGENTA_BORDER = 'rgba(153, 86, 126, 0.3)';

interface TableTypeConfig {
    shape: TableShape;
    seats: number;
    labelKey: string;
}

const TABLE_TYPES: TableTypeConfig[] = [
    { shape: 'round', seats: 4, labelKey: 'tableMaps.wizard.roundTable' },
    { shape: 'square', seats: 4, labelKey: 'tableMaps.wizard.squareTable' },
    { shape: 'rectangle', seats: 8, labelKey: 'tableMaps.wizard.rectangleTable' },
];

// Preview card rendered pixel size — figure is scaled via viewBox
const CARD_SVG_W = 100;
const CARD_SVG_H = 100;

const useStyles = makeStyles({
    root: {
        padding: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 20,
    },
    body: {
        display: 'flex',
        gap: 0,
        alignItems: 'flex-start',
    },
    leftPanel: {
        width: 272,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        paddingRight: 24,
        boxSizing: 'border-box' as const,
    },
    panelHeading: {
        fontSize: 13,
        fontWeight: 600,
        color: theme.palette.text.secondary,
        letterSpacing: 0.5,
        textTransform: 'uppercase' as const,
        marginBottom: 4,
    },
    typeCard: {
        border: `1px solid ${theme.palette.grey['300']}`,
        borderRadius: 8,
        padding: '12px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        cursor: 'grab',
        backgroundColor: 'white',
        userSelect: 'none' as const,
        transition: 'box-shadow 0.15s',
        '&:hover': {
            boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
        },
        '&:active': {
            cursor: 'grabbing',
        },
    },
    typeCardLabel: {
        fontSize: 13,
        fontWeight: 600,
        color: theme.palette.text.primary,
    },
    typeCardSvg: {
        flexShrink: 0,
    },
    canvasColumn: {
        flex: 1,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
    },
    rightSpacer: {
        width: 272,
        flexShrink: 0,
    },
    infoBanner: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 16px',
        borderRadius: 6,
        backgroundColor: MAGENTA_LIGHT,
        border: `1px solid ${MAGENTA_BORDER}`,
    },
    infoBannerIcon: {
        color: MAGENTA,
        fontSize: 20,
        flexShrink: 0,
    },
    infoBannerText: {
        fontSize: 13,
        color: MAGENTA,
        fontWeight: 500,
    },
    toolbar: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    toolbarLeft: {
        display: 'flex',
        alignItems: 'center',
    },
    deleteButton: {
        color: ERROR_COLOR,
        textTransform: 'none' as const,
        fontWeight: 600,
        fontSize: 13,
        padding: '4px 8px',
        '&:hover': {
            backgroundColor: 'transparent',
            textDecoration: 'underline',
        },
    },
    deleteIcon: {
        fontSize: 18,
        color: ERROR_COLOR,
    },
    zoomControls: {
        display: 'flex',
        alignItems: 'center',
        border: `1px solid ${theme.palette.grey['300']}`,
        borderRadius: 4,
        overflow: 'hidden',
    },
    zoomButton: {
        width: 32,
        height: 32,
        border: 'none',
        background: 'white',
        cursor: 'pointer',
        fontSize: 18,
        fontWeight: 600,
        color: theme.palette.text.secondary,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        '&:hover': {
            backgroundColor: theme.palette.grey['300'],
        },
        '&:disabled': {
            color: theme.palette.grey['400'],
            cursor: 'not-allowed',
        },
    },
    zoomLabel: {
        minWidth: 48,
        textAlign: 'center' as const,
        fontSize: 13,
        fontWeight: 600,
        color: theme.palette.text.secondary,
        borderLeft: `1px solid ${theme.palette.grey['300']}`,
        borderRight: `1px solid ${theme.palette.grey['300']}`,
        lineHeight: '32px',
        userSelect: 'none' as const,
    },
    canvasWrapper: {
        width: '100%',
        height: 440,
        borderRadius: 8,
        overflow: 'hidden',
        border: `1px solid ${theme.palette.grey['300']}`,
        position: 'relative' as const,
    },
});

function clientToSVGPoint(
    svg: SVGSVGElement,
    clientX: number,
    clientY: number
): { x: number; y: number } {
    const ctm = svg.getScreenCTM();
    if (!ctm) return { x: 0, y: 0 };
    const inv = ctm.inverse();
    const pt = svg.createSVGPoint();
    pt.x = clientX;
    pt.y = clientY;
    const svgPt = pt.matrixTransform(inv);
    return { x: svgPt.x, y: svgPt.y };
}

/** Clamp a table's normalized-global center (nx,ny) to inside the area shape. Centre only — see report. */
function clampCenterToArea(
    nx: number,
    ny: number,
    size: SizeMeters,
    area: EditorArea
): { x: number; y: number } {
    const metres = { x: nx * size.width, y: ny * size.height };
    const clamped = snapPointMeters(clampPointToArea(metres, area));
    return { x: clamped.x / size.width, y: clamped.y / size.height };
}

/** Convert SVG point to normalized global-rect coords (0..1) */
function svgToNormalized(svgX: number, svgY: number, room: RoomRect): { nx: number; ny: number } {
    return {
        nx: (svgX - room.x) / room.width,
        ny: (svgY - room.y) / room.height,
    };
}

interface PlacedTableProps {
    table: EditorTable;
    room: RoomRect;
    svgRef: React.RefObject<SVGSVGElement>;
    isSelected: boolean;
    onSelect: (id: string) => void;
    onDragEnd: (id: string, x: number, y: number) => void;
}

const PlacedTable = React.memo(function PlacedTable({
    table,
    room,
    svgRef,
    isSelected,
    onSelect,
    onDragEnd,
}: PlacedTableProps) {
    const { draft } = useTableMapWizard();
    const gRef = useRef<SVGGElement>(null);

    const dragActive = useRef(false);
    const didDrag = useRef(false);
    const dragStart = useRef({
        startNx: 0,
        startNy: 0,
        startPointerSvgX: 0,
        startPointerSvgY: 0,
    });

    const { width: figW, height: figH } = tableFootprintPx(
        table.shape,
        table.width,
        table.height,
        draft.tableDefaults,
        room
    );
    const cx = room.x + table.x * room.width;
    const cy = room.y + table.y * room.height;

    const handlePointerDown = useCallback(
        (e: React.PointerEvent<SVGGElement>) => {
            e.stopPropagation();
            if (!svgRef.current) return;
            const gEl = e.currentTarget as SVGGElement;
            gEl.setPointerCapture(e.pointerId);
            const pt = clientToSVGPoint(svgRef.current, e.clientX, e.clientY);
            dragActive.current = true;
            didDrag.current = false;
            dragStart.current = {
                startNx: table.x,
                startNy: table.y,
                startPointerSvgX: pt.x,
                startPointerSvgY: pt.y,
            };
            gEl.style.cursor = 'grabbing';
        },
        [table.x, table.y, svgRef]
    );

    const handlePointerMove = useCallback(
        (e: React.PointerEvent<SVGGElement>) => {
            if (!dragActive.current || !svgRef.current) return;
            const pt = clientToSVGPoint(svgRef.current, e.clientX, e.clientY);
            const dsvgX = pt.x - dragStart.current.startPointerSvgX;
            const dsvgY = pt.y - dragStart.current.startPointerSvgY;

            if (!didDrag.current && Math.hypot(dsvgX, dsvgY) > DRAG_THRESHOLD) {
                didDrag.current = true;
            }

            if (didDrag.current) {
                const dnx = dsvgX / room.width;
                const dny = dsvgY / room.height;
                const clamped = clampCenterToArea(
                    dragStart.current.startNx + dnx,
                    dragStart.current.startNy + dny,
                    draft.size,
                    draft.area
                );
                if (gRef.current) {
                    const newCx = room.x + clamped.x * room.width;
                    const newCy = room.y + clamped.y * room.height;
                    gRef.current.setAttribute('transform', `translate(${newCx}, ${newCy})`);
                }
            }
        },
        [svgRef, room, draft.size, draft.area]
    );

    const handlePointerUp = useCallback(
        (e: React.PointerEvent<SVGGElement>) => {
            if (!dragActive.current || !svgRef.current) return;
            const gEl = e.currentTarget as SVGGElement;
            gEl.style.cursor = 'grab';
            dragActive.current = false;

            if (!didDrag.current) {
                onSelect(table.id);
            } else {
                const pt = clientToSVGPoint(svgRef.current, e.clientX, e.clientY);
                const dsvgX = pt.x - dragStart.current.startPointerSvgX;
                const dsvgY = pt.y - dragStart.current.startPointerSvgY;
                const dnx = dsvgX / room.width;
                const dny = dsvgY / room.height;
                const clamped = clampCenterToArea(
                    dragStart.current.startNx + dnx,
                    dragStart.current.startNy + dny,
                    draft.size,
                    draft.area
                );
                onDragEnd(table.id, clamped.x, clamped.y);
            }
        },
        [svgRef, room, draft.size, draft.area, table.id, onSelect, onDragEnd]
    );

    return (
        <g
            ref={gRef}
            transform={`translate(${cx}, ${cy})`}
            style={{ cursor: 'grab' }}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
        >
            <TableFigure
                width={figW}
                height={figH}
                seats={table.seats}
                shape={table.shape}
                label={table.label}
                isActive={table.isActive}
                rotation={table.rotation}
            />
            {isSelected && <SelectionHighlight figW={figW} figH={figH} />}
        </g>
    );
});

export default function TablePlacementStep() {
    const classes = useStyles();
    const { draft, dispatch } = useTableMapWizard();
    const room = useMemo(() => computeRoom(draft.size), [draft.size.width, draft.size.height]);

    // One shared viewBox across all palette cards so a rectangle table visibly
    // draws wider than a round one, instead of each card filling its own box.
    const paletteViewBox = useMemo(() => {
        let maxHalf = 0;
        for (const config of TABLE_TYPES) {
            const { width: figW, height: figH } = tableFootprintPx(
                config.shape,
                0,
                0,
                draft.tableDefaults,
                room
            );
            const base = Math.min(figW, figH);
            const chairSize = base * 0.15;
            const chairGap = base * 0.1;
            maxHalf = Math.max(
                maxHalf,
                figW / 2 + chairGap + 2 * chairSize,
                figH / 2 + chairGap + 2 * chairSize
            );
        }
        const s = maxHalf * 1.25;
        return `${-s} ${-s} ${2 * s} ${2 * s}`;
    }, [room, draft.tableDefaults]);

    const [zoom, setZoom] = useState(1.0);
    const svgRef = useRef<SVGSVGElement>(null);
    const [selectedTableId, setSelectedTableId] = useState<string | null>(null);

    const [placingConfig, setPlacingConfig] = useState<TableTypeConfig | null>(null);
    const placingConfigRef = useRef<TableTypeConfig | null>(null);

    const ghostGRef = useRef<SVGGElement>(null);
    const pendingPosRef = useRef<{ x: number; y: number }>({ x: 0.5, y: 0.5 });
    const pendingLabelRef = useRef<string>('');

    const handleTableDragEnd = useCallback(
        (id: string, x: number, y: number) => {
            dispatch({ type: 'UPDATE_TABLE_POSITION', payload: { id, x, y } });
        },
        [dispatch]
    );

    const handleSelect = useCallback((id: string) => {
        setSelectedTableId((prev) => (prev === id ? null : id));
    }, []);

    const handleDelete = useCallback(
        (id: string) => {
            dispatch({ type: 'REMOVE_TABLE', payload: { id } });
            setSelectedTableId(null);
        },
        [dispatch]
    );

    function handleZoomIn() {
        setZoom((prev) => Math.min(ZOOM_MAX, parseFloat((prev + ZOOM_STEP).toFixed(2))));
    }

    function handleZoomOut() {
        setZoom((prev) => Math.max(ZOOM_MIN, parseFloat((prev - ZOOM_STEP).toFixed(2))));
    }

    function getSvgPoint(clientX: number, clientY: number): { x: number; y: number } {
        if (!svgRef.current) return { x: 0, y: 0 };
        return clientToSVGPoint(svgRef.current, clientX, clientY);
    }

    function handlePalettePointerDown(
        config: TableTypeConfig,
        e: React.PointerEvent<HTMLDivElement>
    ) {
        e.preventDefault();
        (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
        pendingLabelRef.current = `T${draft.tables.length + 1}`;
        placingConfigRef.current = config;
        if (ghostGRef.current) {
            const svgPt = getSvgPoint(e.clientX, e.clientY);
            const { nx, ny } = svgToNormalized(svgPt.x, svgPt.y, room);
            const clamped = clampCenterToArea(nx, ny, draft.size, draft.area);
            pendingPosRef.current = clamped;
            const gcx = room.x + clamped.x * room.width;
            const gcy = room.y + clamped.y * room.height;
            ghostGRef.current.setAttribute('transform', `translate(${gcx}, ${gcy})`);
        }
        setPlacingConfig(config);
    }

    function handlePalettePointerMove(e: React.PointerEvent<HTMLDivElement>) {
        const config = placingConfigRef.current;
        if (!config || !ghostGRef.current) return;
        const svgPt = getSvgPoint(e.clientX, e.clientY);
        const { nx, ny } = svgToNormalized(svgPt.x, svgPt.y, room);
        const clamped = clampCenterToArea(nx, ny, draft.size, draft.area);
        pendingPosRef.current = clamped;
        const cx = room.x + clamped.x * room.width;
        const cy = room.y + clamped.y * room.height;
        ghostGRef.current.setAttribute('transform', `translate(${cx}, ${cy})`);
    }

    function handlePalettePointerUp(e: React.PointerEvent<HTMLDivElement>) {
        const config = placingConfigRef.current;
        placingConfigRef.current = null;
        if (ghostGRef.current) {
            ghostGRef.current.setAttribute('transform', 'translate(-9999, -9999)');
        }
        setPlacingConfig(null);

        if (!config) return;

        const svgPt = getSvgPoint(e.clientX, e.clientY);
        const overRoom =
            svgPt.x >= room.x &&
            svgPt.x <= room.x + room.width &&
            svgPt.y >= room.y &&
            svgPt.y <= room.y + room.height;

        if (overRoom) {
            const { nx, ny } = svgToNormalized(svgPt.x, svgPt.y, room);
            const clamped = clampCenterToArea(nx, ny, draft.size, draft.area);
            const defaultSize = draft.tableDefaults[config.shape];
            const newTable: Omit<EditorTable, 'qrCode'> = {
                id: `table-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
                label: pendingLabelRef.current,
                shape: config.shape,
                seats: config.seats,
                x: clamped.x,
                y: clamped.y,
                width: defaultSize.width,
                height: defaultSize.height,
                rotation: 0,
                isActive: true,
            };
            dispatch({ type: 'ADD_TABLE', payload: newTable });
        }
    }

    const zoomPercent = Math.round(zoom * 100);

    const placedTablesLayer = useMemo(
        () =>
            draft.tables.map((table) => (
                <PlacedTable
                    key={table.id}
                    table={table}
                    room={room}
                    svgRef={svgRef}
                    isSelected={table.id === selectedTableId}
                    onSelect={handleSelect}
                    onDragEnd={handleTableDragEnd}
                />
            )),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [draft.tables, room, selectedTableId, handleSelect, handleTableDragEnd]
    );

    return (
        <div className={classes.root}>
            <SectionTitle>{i18n.t('tableMaps.wizard.tablePlacement')}</SectionTitle>

            <div className={classes.infoBanner}>
                <InfoOutlinedIcon className={classes.infoBannerIcon} />
                <Typography className={classes.infoBannerText}>
                    {i18n.t('tableMaps.wizard.dragTablesHint')}
                </Typography>
            </div>

            <div className={classes.body}>
                <div className={classes.leftPanel}>
                    <Typography className={classes.panelHeading}>
                        {i18n.t('tableMaps.wizard.tableTypes')}
                    </Typography>
                    {TABLE_TYPES.map((config) => {
                        const { width: figW, height: figH } = tableFootprintPx(
                            config.shape,
                            0,
                            0,
                            draft.tableDefaults,
                            room
                        );

                        return (
                            <div
                                key={config.shape}
                                className={classes.typeCard}
                                onPointerDown={(e) => handlePalettePointerDown(config, e)}
                                onPointerMove={handlePalettePointerMove}
                                onPointerUp={handlePalettePointerUp}
                            >
                                <svg
                                    width={CARD_SVG_W}
                                    height={CARD_SVG_H}
                                    viewBox={paletteViewBox}
                                    className={classes.typeCardSvg}
                                >
                                    <TableFigure
                                        width={figW}
                                        height={figH}
                                        seats={config.seats}
                                        shape={config.shape}
                                    />
                                </svg>
                                <Typography className={classes.typeCardLabel}>
                                    {i18n.t(config.labelKey)}
                                </Typography>
                            </div>
                        );
                    })}
                </div>

                <div className={classes.canvasColumn}>
                    <div className={classes.toolbar}>
                        <div className={classes.toolbarLeft}>
                            {selectedTableId && (
                                <Button
                                    className={classes.deleteButton}
                                    disableRipple
                                    onClick={() => handleDelete(selectedTableId)}
                                    startIcon={<DeleteOutlineIcon className={classes.deleteIcon} />}
                                >
                                    {i18n.t('tableMaps.wizard.delete')}
                                </Button>
                            )}
                        </div>
                        <div className={classes.zoomControls}>
                            <button
                                className={classes.zoomButton}
                                onClick={handleZoomOut}
                                disabled={zoom <= ZOOM_MIN}
                                type="button"
                                aria-label="Zoom out"
                            >
                                −
                            </button>
                            <span className={classes.zoomLabel}>{zoomPercent}%</span>
                            <button
                                className={classes.zoomButton}
                                onClick={handleZoomIn}
                                disabled={zoom >= ZOOM_MAX}
                                type="button"
                                aria-label="Zoom in"
                            >
                                +
                            </button>
                        </div>
                    </div>

                    <div
                        className={classes.canvasWrapper}
                        style={{
                            cursor: placingConfig ? 'crosshair' : 'default',
                        }}
                    >
                        <MapEditorCanvas
                            gridEnabled={draft.gridEnabled}
                            zoom={zoom}
                            size={draft.size}
                            area={draft.area}
                            entrance={draft.entrance}
                            isMarkingEntrance={false}
                            onEntranceChange={() => undefined}
                            svgRef={svgRef}
                        >
                            <rect
                                x={room.x}
                                y={room.y}
                                width={room.width}
                                height={room.height}
                                fill="transparent"
                                onPointerDown={() => setSelectedTableId(null)}
                            />
                            {placedTablesLayer}
                            <g
                                ref={ghostGRef}
                                transform="translate(-9999, -9999)"
                                opacity={0.7}
                                pointerEvents="none"
                            >
                                {placingConfig &&
                                    (() => {
                                        const { width, height } = tableFootprintPx(
                                            placingConfig.shape,
                                            0,
                                            0,
                                            draft.tableDefaults,
                                            room
                                        );
                                        return (
                                            <TableFigure
                                                width={width}
                                                height={height}
                                                seats={placingConfig.seats}
                                                shape={placingConfig.shape}
                                                label={pendingLabelRef.current}
                                            />
                                        );
                                    })()}
                            </g>
                        </MapEditorCanvas>
                    </div>
                </div>

                <div className={classes.rightSpacer} />
            </div>
        </div>
    );
}
